import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom'; // useNavigate for redirect
import { IMAGES } from '../constants/theme';
import NewsLetter from '../elements/NewsLetter';
import PageTitle from '../elements/PageTitle';

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const API_URL = `${API_BASE}/courses/packs`;

// Helper function to validate JWT token expiration
const isTokenValid = (token) => {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiry = payload.exp;
    return expiry && expiry > Date.now() / 1000;
  } catch (e) {
    return false;
  }
};

const Pricing = () => {
  const { id } = useParams(); // get pack id from URL
  const navigate = useNavigate();

  const [hoverEffect, setHoverEffect] = useState(null);
  const [pack, setPack] = useState(null);
  const [promotion, setPromotion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [pendingOrders, setPendingOrders] = useState([]);

  // Helper function to check if promotion is still valid
  const isPromotionValid = (promo) => {
    if (!promo) return false;
    
    const now = new Date();
    
    // Check if promotion has started (valid_from)
    if (promo.valid_from) {
      const validFrom = new Date(promo.valid_from);
      if (now < validFrom) return false;
    }
    
    // Check if promotion has expired (valid_to)
    if (promo.valid_to) {
      const validTo = new Date(promo.valid_to);
      // Set time to end of day for valid_to comparison
      validTo.setHours(23, 59, 59, 999);
      if (now > validTo) return false;
    }
    
    return true;
  };

  useEffect(() => {
    const fetchPack = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${API_URL}/${id}`);
        if (!response.ok) {
          throw new Error(`Error fetching pack: ${response.statusText}`);
        }
        const data = await response.json();
        setPack(data);
        setPromotion(data.promotion || null);
        setHoverEffect(0); // set first plan active by default
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    // Expire old pending payments (older than 5 minutes)
    const expireOldPendingPayments = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        await fetch(`${API_BASE}/courses/pay/expire-old-pending`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (err) {
        console.error("Error expiring old pending payments:", err);
      }
    };

    // Check for pending orders
    const checkPendingOrders = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        // First, expire old pending payments
        await expireOldPendingPayments();

        const res = await fetch(`${API_BASE}/courses/packs/pay/pending`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.ok) {
          const data = await res.json();
          const pendingOrdersList = data.pendingOrders || [];
          const filteredOrders = pendingOrdersList.filter(o => o.packId === id);
          
          // Check if order in localStorage still exists in pending list
          const pendingOrder = JSON.parse(localStorage.getItem('pendingPackOrder') || '{}');
          if (pendingOrder.orderId) {
            const orderExists = filteredOrders.some(order => 
              order.orderId === pendingOrder.orderId
            );
            if (!orderExists) {
              // Order doesn't exist in pending list, likely failed or completed - clear it
              localStorage.removeItem('pendingPackOrder');
              console.log('Cleared localStorage - order no longer pending (likely failed or completed)');
            }
          }
          
          if (filteredOrders.length > 0) {
            setPendingOrders(filteredOrders);
          } else {
            setPendingOrders([]);
          }
        }
      } catch (err) {
        console.error("Error checking pending orders:", err);
      }
    };

    if (id) {
      fetchPack();
      checkPendingOrders();
    }
  }, [id]);

  // Handle PayPal return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token') || urlParams.get('orderId');
    const packId = urlParams.get('packId');
    const plan = urlParams.get('plan');

    if (token && packId && plan) {
      const pendingOrder = JSON.parse(localStorage.getItem('pendingPackOrder') || '{}');
      if (pendingOrder.orderId) {
        handlePayPalReturn(pendingOrder.orderId, packId, plan);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePayPalReturn = async (orderId, packId, plan) => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      // Get user ID
      const userRes = await fetch(`${API_BASE}/users/me`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!userRes.ok) {
        throw new Error("Failed to fetch user data");
      }

      const userData = await userRes.json();
      const userId = userData.user.id || userData.user._id;

      if (!userId) {
        throw new Error("User ID not found");
      }

      // Capture PayPal payment
      const res = await fetch(`${API_BASE}/courses/packs/pay/capture`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          orderId: orderId,
          userId: userId,
          packId: packId,
          plan: plan
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        // Check if order needs approval
        if (data.error === 'ORDER_NOT_APPROVED' && data.approvalUrl) {
          // Redirect to PayPal approval page
          window.location.href = data.approvalUrl;
          return;
        }
        throw new Error(data.error || data.message || "Failed to capture payment");
      }

      // Clear pending order
      localStorage.removeItem('pendingPackOrder');
      setPendingOrders([]);

      // Get categoryId from response or fetch pack data
      const categoryId = data.pack?.category_id;
      
      // Refresh subscription status
      window.history.replaceState({}, document.title, window.location.pathname);
      
      if (categoryId) {
        // Redirect to courses page
        navigate(`/Categories/Category/${categoryId}/Pack/${packId}/Courses`);
      } else {
        // Fallback: fetch pack to get categoryId
        try {
          const packRes = await fetch(`${API_BASE}/courses/packs/${packId}`);
          if (packRes.ok) {
            const packData = await packRes.json();
            const catId = packData.category_id;
            if (catId) {
              navigate(`/Categories/Category/${catId}/Pack/${packId}/Courses`);
            } else {
              navigate(`/packs/${packId}`);
            }
          } else {
            navigate(`/packs/${packId}`);
          }
        } catch (fetchErr) {
          console.error('Error fetching pack data:', fetchErr);
          navigate(`/packs/${packId}`);
        }
      }
      
    } catch (err) {
      console.error("Error capturing PayPal payment:", err);
      setError(err.message);
    }
  };

  const handleManualCapture = async (orderId, packId, plan) => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      setLoading(true);
      
      // Get user ID
      const userRes = await fetch(`${API_BASE}/users/me`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!userRes.ok) {
        throw new Error("Failed to fetch user data");
      }

      const userData = await userRes.json();
      const userId = userData.user.id || userData.user._id;

      if (!userId) {
        throw new Error("User ID not found");
      }

      // Capture PayPal payment
      const res = await fetch(`${API_BASE}/courses/packs/pay/capture`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          orderId: orderId,
          userId: userId,
          packId: packId,
          plan: plan
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        // Check if order needs approval
        if (data.error === 'ORDER_NOT_APPROVED' && data.approvalUrl) {
          // Redirect to PayPal approval page
          window.location.href = data.approvalUrl;
          return;
        }
        throw new Error(data.error || data.message || "Failed to capture payment");
      }

      // Clear pending order
      localStorage.removeItem('pendingPackOrder');
      setPendingOrders([]);

      // Get categoryId from response or fetch pack data
      const categoryId = data.pack?.category_id;
      
      if (categoryId) {
        // Redirect to courses page
        navigate(`/Categories/Category/${categoryId}/Pack/${packId}/Courses`);
      } else {
        // Fallback: fetch pack to get categoryId
        try {
          const packRes = await fetch(`${API_BASE}/courses/packs/${packId}`);
          if (packRes.ok) {
            const packData = await packRes.json();
            const catId = packData.category_id;
            if (catId) {
              navigate(`/Categories/Category/${catId}/Pack/${packId}/Courses`);
            } else {
              navigate(`/packs/${packId}`);
            }
          } else {
            navigate(`/packs/${packId}`);
          }
        } catch (fetchErr) {
          console.error('Error fetching pack data:', fetchErr);
          navigate(`/packs/${packId}`);
        }
      }
      
    } catch (err) {
      console.error("Error capturing PayPal payment:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle Purchase Now click - create PayPal order
  const handlePurchaseClick = async (planLabel) => {
    const token = localStorage.getItem('token');
    
    if (!token || !isTokenValid(token)) {
      
        navigate('/login');
      
      return;
    }

    if (!id) {
      alert("Pack information is loading. Please try again in a moment.");
      return;
    }

    // If there are pending orders, allow retry
    const pendingOrder = JSON.parse(localStorage.getItem('pendingPackOrder') || '{}');
    // Check if order in localStorage exists in pending orders (backend only returns pending, not failed)
    const orderExists = pendingOrder.orderId && 
      pendingOrders.some(order => order.orderId === pendingOrder.orderId);
    if (orderExists || pendingOrders.length > 0) {
      console.log('Creating new payment attempt. Old pending payment will be marked as failed.');
    }

    try {
      setProcessing(planLabel);
      setError(null);

      // Clear old pending orders before creating new one
      localStorage.removeItem('pendingPackOrder');
      setPendingOrders([]);

      // Create PayPal order using /packs/pay endpoint
      const res = await fetch(`${API_BASE}/courses/packs/pay`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          packId: id,
          planLabel: planLabel 
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to create payment order");
      }

      // Store orderId and packId in localStorage for capture after PayPal return
      if (data.orderId) {
        localStorage.setItem('pendingPackOrder', JSON.stringify({
          orderId: data.orderId,
          packId: id,
          plan: planLabel
        }));
      }

      // Redirect to PayPal approval URL
      if (data.approvalUrl) {
        window.location.href = data.approvalUrl;
      } else {
        throw new Error("No approval URL received from PayPal");
      }
    } catch (err) {
      console.error("Error creating PayPal order:", err);
      setError(err.message);
      alert(`Payment Error: ${err.message}`);
      setProcessing(null);
    }
  };

  if (loading) return <div>Loading pricing...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!pack) return <div>No pack found.</div>;

  return (
    <>
      <div className="page-content bg-white">
        <PageTitle activePage={'Pricing'} parentTitle="Category Package Purchase" />

        {error && !error.includes('Payment pending') && (
          <div className="alert alert-danger text-center">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Show promotion banner */}
        {promotion && isPromotionValid(promotion) && (
          <div className="alert alert-success text-center">
            🎉 <strong>Promotion Active!</strong> {promotion.value}%  —{" "}
            <em>{promotion.type}</em>
          </div>
        )}

        {/* Show pending payment notice only if there's an actual pending order (not failed) */}
        {(() => {
          const pendingOrder = JSON.parse(localStorage.getItem('pendingPackOrder') || '{}');
          // Backend only returns orders with status: 'pending', so if we have orders, they're pending
          // Also verify the orderId in localStorage exists in the pending orders list
          const orderInLocalStorageExists = pendingOrder.orderId && 
            pendingOrders.some(order => order.orderId === pendingOrder.orderId);
          const hasPending = orderInLocalStorageExists || pendingOrders.length > 0;
          
          if (hasPending) {
            return (
              <div className="alert alert-warning text-center">
                <strong>⚠ Payment Pending</strong>
                <br />
                <p className="mb-2">You have a pending payment. You can either:</p>
                <div className="d-flex flex-column flex-md-row gap-2 justify-content-center align-items-center">
                  {pendingOrders.map((order, idx) => (
                    <div key={idx} className="mb-2">
                      <button 
                        onClick={() => handleManualCapture(order.orderId, order.packId, order.plan)}
                        className="btn btn-primary btn-sm"
                        disabled={loading}
                      >
                        {loading ? "Processing..." : "Complete Payment"}
                      </button>
                    </div>
                  ))}
                  <span className="mx-2">OR</span>
                  <span className="text-muted small">
                    Click "Purchase Now" below to create a new payment attempt
                  </span>
                </div>
              </div>
            );
          }
          return null;
        })()}

        <section
          className="content-inner rounded-shape-top overflow-hidden"
          style={{ backgroundImage: `url(${IMAGES.BgImage1})` }}
        >
          <div className="container">
            <div className="row">
              {pack.plans.map((plan, ind) => {
                // Use correct field names from backend: discounted_price_cents and original_price_cents
                const finalPrice = plan.discounted_price_cents || plan.price_cents_final || plan.price_cents;
                const originalPrice = plan.original_price_cents || plan.price_cents_original || plan.price_cents;
                const hasDiscount = finalPrice < originalPrice && promotion && isPromotionValid(promotion);
                
                return (
                  <div className="col-lg-4 col-md-6 m-b30" key={plan._id}>
                    <div
                      className={`pricingtable-wrapper box-hover style-1 ${
                        ind === hoverEffect ? 'active' : ''
                      }`}
                      onMouseEnter={() => setHoverEffect(ind)}
                    >
                      <div className="pricingtable-inner">
                        <div className="pricingtable-title">{plan.label}</div>
                        <div className="pricingtable-price">
                          <h2 className="pricingtable-bx text-primary">
                            ${(finalPrice / 100).toFixed(2)}
                            {hasDiscount && (
                              <>
                                <br />
                                <small 
                                  className="text-muted" 
                                  style={{ 
                                    textDecoration: 'line-through', 
                                    fontSize: '0.7em', 
                                    display: 'block',
                                    marginTop: '5px',
                                    fontWeight: 'normal'
                                  }}
                                >
                                  ${(originalPrice / 100).toFixed(2)}
                                </small>
                              </>
                            )}
                          </h2>
                          <p>
                            {plan.label === '1 Month'
                              ? 'Perfect for trying out our services for a short time.'
                              : plan.label === '3 Months'
                              ? 'Ideal for a more comprehensive and steady transformation.'
                              : 'A good choice when working remotely With Your Clients'}
                          </p>
                        </div>
                        <ul className="pricingtable-features">
                          {plan.label === '1 Month' ? (
                            <>
                              <li>Quick Start Guide</li>
                              <li>Daily Workout Plans</li>
                              <li>Basic Nutrition Tips</li>
                              <li>Community Support Access</li>
                              <li>Progress Tracking</li>
                            </>
                          ) : plan.label === '3 Months' ? (
                            <>
                              <li>Comprehensive Training Program</li>
                              <li>Work with Resources</li>
                              <li>Weekly Coaching Sessions</li>
                              <li>Advanced Progress Analytics</li>
                              <li>Support & Mentoring</li>
                            </>
                          ) : (
                            <>
                              <li>Review Your Question</li>
                              <li>Work with Resources</li>
                              <li>Social Media Marketing</li>
                              <li>Analysis of Your "I Have"</li>
                              <li>Support & Mentoring</li>
                            </>
                          )}
                        </ul>
                        <div className="pricingtable-footer">
                          <button
                            onClick={() => handlePurchaseClick(plan.label)}
                            className="btn btn-primary btn-skew"
                            disabled={processing === plan.label || loading}
                          >
                            <span>
                              {processing === plan.label
                                ? "Processing..."
                                : (() => {
                                    const pendingOrder = JSON.parse(localStorage.getItem('pendingPackOrder') || '{}');
                                    // Only show "Retry Payment" if order exists in pending orders list (not failed)
                                    const orderExists = pendingOrder.orderId && 
                                      pendingOrders.some(order => order.orderId === pendingOrder.orderId);
                                    const hasPending = orderExists || pendingOrders.length > 0;
                                    return hasPending ? "Retry Payment" : "Purchase Now";
                                  })()}
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
        <section className="call-action style-1 footer-action">
          <div className="container">
            <NewsLetter />
          </div>
        </section>
      </div>
    </>
  );
};

export default Pricing;
